import React from 'react'
import styled from 'styled-components'
import { Formik, FormikHelpers, FormikProps } from 'formik'
import * as yup from 'yup'

import { SKETCH_WEBSITE } from '@sketch/env-config'

import {
  Button,
  Checkbox,
  Link,
  Form,
  Input,
  CreatePasswordInput,
  passwordStrengthValidation,
  Text,
} from '@sketch/components'

import { isPricingSourceRoute, validEmail } from '@sketch/utils'

import {
  setUserAuthorization,
  createPersonalAuthorization,
} from '../../libs/authentication'
import {
  useAnalytics,
  SignupBaseProps as SignupAnalyticsEventBaseProps,
} from '../../analytics'

import { useTrackEditedFields } from './useTrackEditedFields'

import { SIGN_UP_INITIAL_VALUES, Values } from './constants'

import { useSignUpMetadata } from './useSignUpMetadata'

import {
  useSignUpMutation,
  GetInitialUserDocument,
  GetInitialUserQuery,
  SignUpMutation,
} from '@sketch/gql-types'
import { DataWithoutUserErrors, ParsedError } from '@sketch/graphql-apollo'
import { useLocation } from 'react-router'

const signUpSchema = yup.object().shape({
  name: yup.string().trim().required('Enter your first name'),
  email: validEmail().required('Enter your email address'),
  password: yup
    .string()
    .min(8, 'Should be 8 or more characters')
    .max(72, 'Should be 72 or less characters')
    .required('Create a new password')
    .test(
      'passwordStrength',
      'The password strength should be at least Okay',
      passwordStrengthValidation
    ),
  'marketing-consent': yup.boolean(),
})

export interface ExtendedFormikProps extends FormikProps<Values> {
  apiError: ParsedError | undefined
  callToAction: string
  formContextForAnalytics: SignupAnalyticsEventBaseProps['formContext']
}

const StyledText = styled(Text).attrs({
  textStyle: 'copy.tertiary.standard.D',
})`
  margin-bottom: 16px;
`

type FieldsProps = ExtendedFormikProps & {
  formContextForAnalytics: SignupAnalyticsEventBaseProps['formContext']
}

export const Fields: React.FCC<FieldsProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  formContextForAnalytics,
}) => {
  const location = useLocation()

  useTrackEditedFields(values, formContextForAnalytics)
  const isPricingRoute = isPricingSourceRoute(location)

  return (
    <>
      <Form.Field
        name="name"
        label="Your first name"
        errorText={touched.name ? errors.name : undefined}
      >
        <Input
          type="text"
          name="name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </Form.Field>
      <Form.Field
        name="email"
        label="Email"
        errorText={touched.email ? errors.email : undefined}
      >
        <Input
          name="email"
          type="email"
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
          autoFocus={!!values.email}
        />
      </Form.Field>
      <CreatePasswordInput
        name="password"
        value={values.password}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={touched.password && !!errors.password}
      />

      <Form.Field name="marketing-consent" mb={4}>
        <Checkbox
          name="marketing-consent"
          checked={Boolean(values['marketing-consent'])}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          Get our newsletter with resources, and&nbsp;more…
        </Checkbox>
      </Form.Field>
      <StyledText>
        By clicking{' '}
        {isPricingRoute ? <>&ldquo;Next&rdquo;</> : <>&ldquo;Sign Up&rdquo;</>},
        you are confirming you have read and agreed with our{' '}
        <Link
          variant="primary"
          isUnderlined
          external
          href={`${SKETCH_WEBSITE}/tos`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms of Service
        </Link>{' '}
        and our{' '}
        <Link
          variant="primary"
          isUnderlined
          external
          href={`${SKETCH_WEBSITE}/privacy`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </Link>
      </StyledText>
    </>
  )
}

export const Errors: React.FCC<ExtendedFormikProps> = ({ apiError, status }) =>
  status || apiError ? (
    <Form.ErrorField>{status || apiError?.message || ''}</Form.ErrorField>
  ) : null

export const DefaultFormFields: React.FCC<ExtendedFormikProps> = formProps => {
  const { trackEvent } = useAnalytics()

  return (
    <>
      <Fields {...formProps} />
      <Errors {...formProps} />
      <Submit
        disabled={!(formProps.isValid && formProps.dirty)}
        callToAction={formProps.callToAction}
        isSubmitting={formProps.isSubmitting}
        onClick={() => {
          const submitCount = formProps.submitCount + 1
          trackEvent('SIGN UP - Sign up form submitted', {
            submitCount: submitCount,
            formContext: formProps.formContextForAnalytics,
          })
        }}
      />
    </>
  )
}
export interface SignUpFormProps {
  email?: string
  /** User has an invitation token */
  invitedUser?: boolean
  children?: React.FCC<ExtendedFormikProps>
  onCompleted?: (data: DataWithoutUserErrors<SignUpMutation>) => void
  onError?: (message: string) => void
  callToAction?: string
  formContextForAnalytics: SignupAnalyticsEventBaseProps['formContext']
}

export interface SubmitProps {
  isSubmitting: boolean
  callToAction?: string
  disabled?: boolean
  onClick?: () => void
}

export function Submit({
  isSubmitting,
  disabled,
  onClick,
  callToAction = 'Sign Up',
}: SubmitProps) {
  return (
    <Form.Footer>
      <Button
        variant="primary"
        type="submit"
        fill
        size="40"
        disabled={isSubmitting || disabled}
        loading={isSubmitting}
        onClick={onClick}
      >
        {callToAction}
      </Button>
    </Form.Footer>
  )
}

const SignUpForm = ({
  email = '',
  invitedUser,
  onCompleted,
  onError,
  formContextForAnalytics,
  children = DefaultFormFields,
  callToAction = 'Sign Up',
}: SignUpFormProps) => {
  const { metadata, clearMetadata: cleanSignUpMetadata } = useSignUpMetadata()

  const [signUp, { loading, error: apiError }] = useSignUpMutation({
    redirectErrors: true,
    update: (cache, { data }) => {
      if (!data?.signUp) {
        return
      }

      /* Save the user credentials in the apollo cache */
      setUserAuthorization(
        cache,
        createPersonalAuthorization(data.signUp!.credentials!)
      )

      /* Save the user profile information */
      cache.writeQuery<GetInitialUserQuery>({
        query: GetInitialUserDocument,
        data: {
          __typename: 'RootQueryType',
          me: data.signUp.user!,
        },
      })
    },
    onCompleted: data => {
      cleanSignUpMetadata()
      onCompleted?.(data)
    },
    onError: ({ message }) => {
      onError?.(message)
    },
  })

  const handleFormSubmit = async (
    values: Values,
    actions: FormikHelpers<Values>
  ) => {
    try {
      actions.setStatus()

      const { 'marketing-consent': marketingConsent, ...otherValues } = values

      await signUp({
        variables: {
          input: {
            ...otherValues,

            /**
             * ToS and Privacy Policy are now implicit agreements
             * https://linear.app/sketch/issue/SWEB-809/replace-tos-checkbox-with-implicit-eula-agreement-statement
             */
            privacyPolicyAgreed: true,
            tosAgreed: true,

            marketingConsent,
            createWorkspace: !invitedUser,
            metadata,
          },
        },
      })
    } catch (e) {
      // The error handling should be taken care of by Apollo
    } finally {
      actions.setSubmitting(false)
    }
  }

  return (
    <>
      <Formik
        initialValues={{
          ...SIGN_UP_INITIAL_VALUES,
          email,
        }}
        onSubmit={handleFormSubmit}
        validationSchema={signUpSchema}
        validateOnBlur={false}
        enableReinitialize
      >
        {formikbag => (
          <Form>
            {
              children({
                ...formikbag,
                isSubmitting: loading,
                apiError,
                callToAction,
                formContextForAnalytics,
              }) as React.ReactNode
            }
          </Form>
        )}
      </Formik>
    </>
  )
}

export default SignUpForm
